import React from "react";
import { Box, Text } from "@chakra-ui/react";
import { type Customer, type IntegrationSimple } from "lib/sanity";
import { sectionSpacingY } from "components/section";
import { DestinationMarquee, SecurityFeatures, TraditionalCdpVsHightouch } from "components/content";
import { CdpAccordion, CdpDiagram } from "components/platform/composable-cdp";
import { UseCaseTabs } from "components/platform-overview-content";
import {
  dataFlexibilityComparison,
  implementationEffortComparison,
  realTimeComparison,
  sourcesOfTruthComparison,
} from "components/content/traditional-cdp-vs-hightouch/data";
import { AuraCardQuote } from "components/quotes";
import { SolidPlate } from "ui/plates";
import { Superscript } from "ui/text";
import { getSafeCustomer } from "utils";

/** Shared template for both the platform and ads pages. */
export function ComposableCdpTemplate({
  destinations,
  customers,
}: {
  destinations: IntegrationSimple[];
  customers: Customer[];
}) {
  const grammarly = getSafeCustomer(customers, "grammarly");

  return (
    <Box>
      <TraditionalCdpVsHightouch
        title={
          <>
            90%
            <Superscript
              size="lg"
              href="https://digiday.com/marketing/the-vast-majority-of-marketers-are-unhappy-with-their-customer-data-platforms-but-then-again-not-many-are-sure-what-they-really-are/"
            />{" "}
            of marketers say their CDP does not do what they need,{" "}
            <Text as="span" color="new.green.500">
              so why do they keep buying them?
            </Text>
          </>
        }
        rows={[
          dataFlexibilityComparison,
          sourcesOfTruthComparison,
          implementationEffortComparison,
          realTimeComparison,
          {
            category: "Product feature bundles",
            traditionalCdp: {
              title: "One size fits all",
              description: "Traditional CDPs offer expensive product bundles with features you may not need.",
            },
            hightouch: {
              title: "Molded to your specific use cases",
              description:
                "Select from a complete set of products and features, but only purchase and implement the ones you need to solve exactly what you are trying to accomplish.",
            },
          },
        ]}
      />

      <UseCaseTabs title="Power any CDP use case, without the usual headaches" />

      <SolidPlate bgColor="new.green.950" spotlightColor="new.green.900" theme="dark">
        <CdpDiagram />
        <CdpAccordion />
      </SolidPlate>

      <SecurityFeatures
        title="Composable CDPs are more secure"
        description="Hightouch doesn’t store your data. Instead we simply read from your data warehouse where it stays safe and sound."
        textMaxW="540px"
        my={0}
        py={sectionSpacingY}
      />

      <AuraCardQuote
        my={0}
        variant="green"
        size="md"
        quote={{
          quoteText: (
            <>
              <Text as="strong" fontWeight="bold">
                If we had to get rid of Hightouch, we’d riot.
              </Text>{" "}
              It underpins our entire marketing funnel and strategy. Marketing is all about personalization, and that’s
              what Hightouch gives us.
            </>
          ),
          author: { name: "Trevor Luescher", job: "Marketing Systems Architecture" },
          company: { name: "Grammarly", logo: grammarly.logoUrl },
          cta: { text: "Read the story", href: "/customers/grammarly" },
        }}
      />

      <DestinationMarquee destinations={destinations} my={0} py={sectionSpacingY} />
    </Box>
  );
}
